import React, { useCallback, useMemo, useState } from 'react';
import { Form, RadioParam, Row, SonForm, SubmitButton, useForm } from '../../../../_lib/Form2';
import { useHtmlValidId } from '../../../../hooks/useHtmlValidId';
import { FieldSet } from '../../../../_lib/Form2/types/FieldSet';
import { Namespace } from '../../../../translation/namespace';
import { MultiStepForm, Step } from '../../../../components/molecules/Form/components/MultiStepForm';
import { AccessLevelChoicesForm } from './AccessLevelChoicesForm';
import { formParamsUtils } from '../../../../_lib/Form2/utils/formParamsUtils';
import { IParameterProperty } from '../../../../types/IParameter';
import usePrevious from '../../../../hooks/usePrevious';
import { useFetch } from '../../../../hooks/useFetch';

import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { AccessLevelInformationForm, AccessLevelInformationParamType } from './AccessLevelInformationForm';
import { useModalContext } from '../../../../components/organisms/ModalDialog/ModalContext';
import { useDelayed } from '../../../../hooks/useDelayed';
import { useInvalidateRefreshableBlock, useRefreshBlock } from '../../../../_lib/BlockFetcher';
import { Hint } from '../../../../_lib/components/Hint';
import { AccessLevel, AccessLevels } from '../types';
import { BlockType } from '../../../../types/api/ElementType';
import { Form2Modifier } from '../../../../_lib/Form2/Form';

const TAB_PANEL_IDS = {
  ACCESS_LEVEL: 'multi-step-form-access-level',
  ACCESS_LEVEL_INFORMATION: 'multi-step-form-page-informations',
};

export type AccessLevelInfoRequestFormTypes = SonForm<FieldSet<'accessLevels', RadioParam<'accessLevel', AccessLevel>>>;

type AccessLevelInforequestFormProps = { form: AccessLevelInfoRequestFormTypes };

export function AccessLevelInfoRequestForm({ form }: AccessLevelInforequestFormProps) {
  const { t } = useSonTranslation('AccessLevel');

  const invalidateRefreshableBlock = useInvalidateRefreshableBlock();
  const { refresh } = useRefreshBlock();
  const { closeModal } = useModalContext();
  const refreshAndClose = useCallback(() => {
    refresh();
    invalidateRefreshableBlock(BlockType.changeAccessLevel);
    closeModal();
  }, [closeModal, refresh, invalidateRefreshableBlock]);
  const delayedRefreshAndClose = useDelayed(refreshAndClose);
  const onSuccess = () => {
    delayedRefreshAndClose();
  };

  const [selectedAccessLevelParameters, setSelectedAccessLevelParameters] = useState<
    RadioParam<'accessLevel', AccessLevel> | undefined
  >(undefined);
  const previousSelectedAccessLevelParameters = usePrevious(selectedAccessLevelParameters);
  const [fetchedFormParameters, setFetchedFormParameters] = useState(formParamsUtils({ params: [] } as any));

  const { get, requestError, isLoading } = useFetch();

  const id = useHtmlValidId();

  const { formProps, params, fetchState } = useForm({ form, onSuccess });

  const fetchAdditionalFormParameters = useCallback(async () => {
    if (
      selectedAccessLevelParameters &&
      selectedAccessLevelParameters.formParamsFetchUrl &&
      selectedAccessLevelParameters.formParamsFetchUrl !== previousSelectedAccessLevelParameters?.formParamsFetchUrl
    ) {
      const res = await get<Record<string, IParameterProperty[]>>(selectedAccessLevelParameters.formParamsFetchUrl);

      if (res) {
        setFetchedFormParameters(
          formParamsUtils({
            params: Object.values(res.data).reduce((acc, curr) => {
              return acc.concat(curr);
            }, [] as any),
          } as any)
        );
      }
    }
  }, [get, previousSelectedAccessLevelParameters?.formParamsFetchUrl, selectedAccessLevelParameters]);

  const renderSubmitAndLiveRegion = useMemo(() => {
    if (selectedAccessLevelParameters?.value) {
      return (
        <SubmitButton
          buttonLabel={t('buttons.submit')}
          messageSuccess={t('form.status.success')}
          messageSending={t('form.status.sending')}
          messageFail={t('form.status.fail')}
          fetchState={fetchState}
          disabled={fetchState.type === 'loading' || fetchState.type === 'success'}
        />
      );
    }

    return null;
  }, [fetchState, selectedAccessLevelParameters?.value, t]);

  return (
    <Form {...formProps} id={`access-level-request-form-${id}`} modifier={Form2Modifier.NO_PADDING}>
      <MultiStepForm
        initialActiveTab={TAB_PANEL_IDS.ACCESS_LEVEL}
        fetchingData={{
          fetchFormParams: fetchAdditionalFormParameters,
          isLoading,
          requestError,
          loadingMessage: t(`${Namespace.phrases}:contentIsLoading`),
        }}
      >
        <Step id={TAB_PANEL_IDS.ACCESS_LEVEL} buttonLabel={t('steps.accessLevel.title')}>
          <AccessLevelChoicesForm
            params={params.getByName('accessLevels')?.params}
            setSelectedAccessLevelParameters={setSelectedAccessLevelParameters}
          />
        </Step>
        <Step
          id={TAB_PANEL_IDS.ACCESS_LEVEL_INFORMATION}
          buttonLabel={t('steps.accessLevelInformation.title')}
          submitButton={renderSubmitAndLiveRegion}
          disabled={!selectedAccessLevelParameters}
        >
          {selectedAccessLevelParameters?.value === AccessLevels.A1 ? (
            <Row>
              <Hint message={t('form.information.a1Hint')} />
            </Row>
          ) : (
            <AccessLevelInformationForm
              accessLevel={selectedAccessLevelParameters?.value}
              params={fetchedFormParameters as AccessLevelInformationParamType}
            />
          )}
        </Step>
      </MultiStepForm>
    </Form>
  );
}
